import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router';

const techFaqs = [
  {
    id: 'faq1',
    question: 'Is dental technology safe?',
    answer:
      'Yes, absolutely. Modern dental technology like digital X-rays, 3D scanners, and lasers are designed with patient safety in mind. For example, digital X-rays emit up to 80% less radiation than traditional X-rays.',
  },
  {
    id: 'faq2',
    question: 'Does advanced technology make treatment more expensive?',
    answer:
      'Not necessarily. While some equipment is high-tech, it often reduces the number of appointments, speeds up treatment, and prevents costly issues—saving you time and money in the long run.',
  },
  {
    id: 'faq3',
    question: 'Will I feel anything during digital scans or 3D imaging?',
    answer:
      'No, digital scans and 3D imaging are completely non-invasive and painless. There\'s no discomfort, and the process is fast and accurate.',
  },
  {
    id: 'faq4',
    question: 'How do intraoral cameras help me as a patient?',
    answer:
      'Intraoral cameras let you see what we see—close-up, high-definition views of your teeth and gums—so you can better understand your dental health and treatment plan.',
  },
  {
    id: 'faq5',
    question: 'What is laser dentistry, and is it better than traditional methods?',
    answer:
      'Laser dentistry uses focused light energy for precise treatment of gums and soft tissue. It\'s minimally invasive, often requires no anesthesia, and promotes faster healing with less bleeding.',
  },
  {
    id: 'faq6',
    question: 'Do digital impressions replace traditional molds?',
    answer:
      'Yes! Digital impressions are faster, cleaner, and far more comfortable than traditional trays. They\'re used for crowns, Invisalign, bridges, and more.',
  },
  {
    id: 'faq7',
    question: 'How does technology improve the accuracy of dental procedures?',
    answer:
      'Technology like 3D imaging and AI-assisted diagnostics allow us to plan treatments with pinpoint precision, reduce guesswork, and deliver more predictable results.',
  },
  {
    id: 'faq8',
    question: 'Is all this technology really necessary?',
    answer:
      'Yes—because your comfort, safety, and long-term oral health are our priorities. Advanced tools help us provide higher quality care with better outcomes and a smoother experience.',
  },
];

export default function DentalTechFaq() {
  const [openQuestion, setOpenQuestion] = useState('faq1');

  const toggleQuestion = (id) => {
    setOpenQuestion(openQuestion === id ? null : id);
  };

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 xl:px-20 py-16 bg-gray-50">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-semibold text-gray-800 mb-3">FAQs About Dental Technology</h2>
        <p className="text-teal-600">Got questions about the tech we use? We've got answers.</p>
      </div>

      <div className="space-y-4">
        {techFaqs.map((faq, index) => (
          <div
            key={faq.id}
            className="opacity-0 animate-fade-in"
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm 
                            hover:shadow-md transition-shadow duration-300 bg-white">
              <button
                className="w-full px-6 py-4 text-left flex justify-between items-center 
                             hover:bg-gray-50 transition-colors duration-300"
                onClick={() => toggleQuestion(faq.id)}
              >
                <span className="text-lg font-medium text-teal-600">{faq.question}</span>
                <span className={`flex items-center justify-center w-8 h-8 rounded-full 
                                  ${openQuestion === faq.id ? 'bg-teal-600' : 'bg-lime-500'} 
                                  transition-colors duration-300`}>
                  <ChevronDown
                    className={`w-5 h-5 text-white transition-transform duration-300 
                               ${openQuestion === faq.id ? 'rotate-180' : ''}`}
                  />
                </span>
              </button>

              <div
                className={`transition-all duration-300 ease-in-out 
                             ${openQuestion === faq.id ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
              >
                <div className="px-6 pb-4 pt-2">
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 text-center">
        <p className="text-gray-500 mb-4">Still have questions?</p>
        <Link
          to='https://wa.me/+918401749379'
          target='_blank' 
          className="bg-teal-600 hover:bg-teal-700 text-white font-medium py-2 px-6 rounded-full shadow-md transition duration-300">
          Contact Us
        </Link>
      </div>
    </div>
  );
}